import { isStandalonePagePath } from './appRouteAccess';

/** 首页由服务端按用户全部角色、部门及菜单权限解析，客户端不推断职责。 */
export interface HomePage {
  menuId?: number;
  name: string;
  path: string;
  source: 'RULE' | 'FALLBACK';
  matchedRuleId?: number;
}

export const HOME_ENTRY_PATH = '/';
export const SAFE_WORKBENCH_PATH = '/workbench';

/** 页面身份忽略路径末尾斜杠，原样保留查询串和锚点。 */
export function normalizeNavigationPath(path: string): string {
  const suffixAt = path.search(/[?#]/);
  const pathname = suffixAt < 0 ? path : path.slice(0, suffixAt);
  const suffix = suffixAt < 0 ? '' : path.slice(suffixAt);
  return (pathname.replace(/\/+$/, '') || '/') + suffix;
}

export function isHomeEntryPath(path: string): boolean {
  const pathname = path.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  return pathname === '/' || pathname === '/dashboard';
}

/** 仅接受 PC 应用内的路径，避免错误配置与登录回跳形成外链或重定向循环。 */
export function isInternalAppPath(path: unknown): path is string {
  if (
    typeof path !== 'string' ||
    !path.startsWith('/') ||
    path.startsWith('//') ||
    path.includes('\\') ||
    Array.from(path).some((character) => character.charCodeAt(0) <= 32)
  )
    return false;
  try {
    const pathname = new URL(path, 'https://home.invalid').pathname;
    return !isStandalonePagePath(pathname);
  } catch {
    return false;
  }
}

export function resolveHomePath(
  homePage?: Pick<HomePage, 'path'> | null,
): string {
  const path = homePage?.path;
  return isInternalAppPath(path) && !isHomeEntryPath(path)
    ? normalizeNavigationPath(path)
    : SAFE_WORKBENCH_PATH;
}

export function resolveLoginDestination(
  redirect: string | null | undefined,
  homePage?: HomePage | null,
): string {
  if (isInternalAppPath(redirect) && !isHomeEntryPath(redirect))
    return redirect;
  return resolveHomePath(homePage);
}

/** 未登录访问深链时保留完整目标；主动退出直接去 /user/login，不调用本方法。 */
export function loginPathForLocation(location: {
  pathname: string;
  search?: string;
  hash?: string;
}): string {
  const target =
    location.pathname + (location.search || '') + (location.hash || '');
  return isInternalAppPath(target) && !isHomeEntryPath(target)
    ? `/user/login?${new URLSearchParams({ redirect: target }).toString()}`
    : '/user/login';
}
